import { Button } from "../../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import { MoreHorizontal, Pencil, Trash2, KeyRound, Users } from "lucide-react";
import { formatCurrency } from "../../lib/currency";

const EmployeeTable = ({
  employees,
  loading,
  getDepartmentName,
  getDesignationName,
  onEdit,
  onDelete,
  onCreateLogin,
  onResetPassword,
  currency,
}) => {
  const fmt = (amount) => formatCurrency(amount, currency);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-500">
        <Users className="h-12 w-12 mb-3 text-slate-300" />
        <p>No employees found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Employee</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Code</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Department</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Designation</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Salary</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map((employee) => (
            <TableRow key={employee.id} className="hover:bg-slate-50/50" data-testid={`employee-row-${employee.id}`}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center text-white font-medium text-sm">
                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{employee.first_name} {employee.last_name}</p>
                    <p className="text-xs text-slate-500">{employee.email}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-slate-600 font-mono text-sm">
                {employee.employee_code || "-"}
              </TableCell>
              <TableCell className="text-slate-600">
                {getDepartmentName(employee.department_id)}
              </TableCell>
              <TableCell className="text-slate-600">
                {getDesignationName(employee.designation_id)}
              </TableCell>
              <TableCell className="text-slate-600">
                {employee.salary ? fmt(employee.salary) : "-"}
              </TableCell>
              <TableCell>
                <Badge className={employee.is_active ? "badge-success" : "badge-danger"}>
                  {employee.is_active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`employee-actions-${employee.id}`}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(employee)} data-testid={`edit-employee-${employee.id}`}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    {!employee.user_id && (
                      <DropdownMenuItem onClick={() => onCreateLogin(employee)} data-testid={`create-login-${employee.id}`}>
                        <KeyRound className="h-4 w-4 mr-2" />
                        Create Login
                      </DropdownMenuItem>
                    )}
                    {employee.user_id && (
                      <DropdownMenuItem onClick={() => onResetPassword(employee)} data-testid={`reset-password-${employee.id}`}>
                        <KeyRound className="h-4 w-4 mr-2" />
                        Reset Password
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => onDelete(employee.id)} 
                      className="text-red-600"
                      data-testid={`delete-employee-${employee.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default EmployeeTable;
